import React, { useEffect, useState } from 'react';
import { FormControl, HStack, Switch, VStack } from 'native-base';
import AmountDropdown from '../dropdowns/Amount';
import PaymentDropdown from '../dropdowns/Payment';
import {
    getMenuItems,
    GiftCardPayment,
    i18nFn,
    observer,
    useActivateGiftCard,
    useActivateGiftCardWithPoints,
    useSpringBigStore, useTheme,
} from '@springbig/shared';
import { Caption, CaptionError } from '~/shared/Typography';
import PointsConverterField from '~/pages/giftCard/pointsConverter/PointsConverterField';
import { Button, Input } from '~/shared/Theme';

const SendToFriend = ({ t }: { t: i18nFn }) => {

    const { member, merchant, giftCardPresetAmounts } = useSpringBigStore();

    const { primary } = useTheme();

    const maxAmount = Math.min(
        Number(member?.remaining_dollar_amount_for_current_period) / 100,
        Number(merchant?.gc_dollar_amount_one_time)
    );

    const minAmount = Math.min(merchant.gc_min_price, 20);

    const [email, setEmail] = useState('');
    const [amount, setAmount] = useState<number>(0); // remove ability to purchase GC altogether
    // const [amount, setAmount] = useState<number>(10);
    const [convertPoints, setConvertPoints] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState<GiftCardPayment | null>(null);
    const [submitError, setSubmitError] = useState('');
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [isActivateWithPoints, setIsActivateWithPoints] = useState(false);

    const emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

    const {
        mutate: requestActivateGiftCard,
        error: requestActivateCardError,
        isLoading: activateGiftCardLoading
    } = useActivateGiftCard();

    const {
        mutate: activateWithPoints,
        error: activateWithPointsError,
        isLoading: activateWithPointsLoading,
    } = useActivateGiftCardWithPoints();

    useEffect(() => {

        requestActivateCardError && setSubmitError(t('general.submitError'));
    }, [requestActivateCardError]);

    const handleEmailChange = (text: string) => {

        setEmail(text);
        setIsValidEmail(emailPattern.test(text));
    };

    const isSubmitEnabled =
        (paymentMethod || isActivateWithPoints ) &&
        (amount || convertPoints) &&
        !activateGiftCardLoading &&
        !activateWithPointsLoading;

    const handleSubmit = () => {

        setIsValidEmail(emailPattern.test(email));

        if (emailPattern.test(email) && isSubmitEnabled) {
            if (isActivateWithPoints) {
                activateWithPoints({
                    auto_reload: false,
                    auto_reload_amount: '20.00',
                    purchase_location: '0',
                    member_id: member?.id,
                    friends_email: email,
                    convert_points: convertPoints,
                }, {
                    onSuccess: (_) => {

                        observer.emit('sent-giftcard', { email, amount: (convertPoints / Number(merchant?.dollars_per_point)).toString() });
                    }
                });
            }
            else {
                requestActivateGiftCard({
                    balance: amount.toString(),
                    auto_reload: false,
                    auto_reload_amount: '20.00',
                    purchase_location: '0',
                    gift_card_payment_id: paymentMethod?.id || 0,
                    member_id: member?.id.toString() || '',
                    friends_email: email
                }, {
                    onSuccess: (_) => {

                        observer.emit('sent-giftcard', { email, amount: amount.toString() });
                    }
                });
            }
        }
    };

    return (
        <VStack space={4}>
            {
                !merchant?.hide_gift_card_payments &&
                <VStack justifyContent={'center'} alignItems={'center'} p={4} bg={'#F48309'}>
                    <Caption color={'white'}>{ merchant?.gift_card_alias } purchases or reloads are temporarily unavailable.</Caption>
                </VStack>
            }
            <Caption
                text={'giftCard.send.title'}
                textValues={[merchant?.gift_card_alias]}
                alignSelf='center'
                fontSize={24}
            />
            {
                merchant?.points_cash_conversion_enabled &&
                merchant?.create_gift_card_with_points &&
                member?.balance >= parseFloat(merchant?.dollars_per_point) &&
                <HStack justifyContent={'space-between'}>
                    <Caption
                        text={'giftCard.addFunds.fundWithPoints'}
                        textValues={[merchant?.gift_card_alias]}
                        fontSize={18}
                    />
                    <Switch
                        onTrackColor={primary}
                        offTrackColor={primary?.slice(0, 7) + '40'}
                        _hover={{
                            onTrackColor: primary?.slice(0, 7) + '40',
                            offTrackColor: primary,
                        }}
                        isChecked={isActivateWithPoints}
                        onToggle={() => setIsActivateWithPoints(!isActivateWithPoints)}
                    />
                </HStack>
            }
            {
                false && // remove ability to purchase GC altogether
                !isActivateWithPoints &&
                merchant &&
                !merchant?.hide_gift_card_payments &&
                <VStack>
                    <Caption
                        text={'general.amount'}
                        fontSize={18}
                    />
                    <AmountDropdown
                        t={t}
                        amount={amount}
                        setAmount={setAmount}
                        amounts={getMenuItems(merchant, giftCardPresetAmounts, maxAmount, minAmount)}
                    />
                </VStack>
            }
            { isActivateWithPoints &&
                <PointsConverterField t={t} onChange={(value) => setConvertPoints(value)} />
            }
            <FormControl isRequired isInvalid={!isValidEmail}>
                <Caption
                    text={'giftCard.send.form.email.title'}
                    fontSize={18}
                />
                <Input
                    placeholder={t('giftCard.send.form.email.title')}
                    onChangeText={handleEmailChange}
                    value={email}
                    keyboardType={'email-address'}
                    autoCapitalize={'none'}
                />
                { !isValidEmail &&
                    <CaptionError>
                        { t('giftCard.send.form.email.error') }
                    </CaptionError>
                }
            </FormControl>
            {
                false && // remove ability to purchase GC altogether
                !isActivateWithPoints &&
                <VStack>
                    <Caption
                        text={'general.payment'}
                        fontSize={18}
                    />
                    <PaymentDropdown
                        paymentMethod={paymentMethod}
                        setPaymentMethod={setPaymentMethod}
                        t={t}
                    />
                </VStack>
            }
            { member?.customer_gift_card_fee && member?.customer_gift_card_fee !== '$0.00' &&
                <Caption
                    text={'giftCard.addFunds.conditions.fees'}
                    textValues={[member?.customer_gift_card_fee]}
                    textAlign='center'
                />
            }
            {
                (
                    requestActivateCardError ||
                    activateWithPointsError
                ) &&
                <CaptionError>
                    {
                        (requestActivateCardError as any)?.data?.message ||
                        (activateWithPointsError as any)?.data?.message
                    }
                </CaptionError>
            }
            <Button
                fontSize={18}
                text={t('general.send') + ` $${ isActivateWithPoints ? convertPoints / parseFloat(merchant?.dollars_per_point || '0.0') : amount }`}
                onPress={handleSubmit}
                disabled={!isSubmitEnabled}
            />
        </VStack>
    );
};

export default SendToFriend;
